/**
 * Block 13 PR-G Phase 5A + 6.5G A8 + 6.5G hotfix (2026-05-25) — Presenter
 * Экрана 2.13.1 «Договор» (ADR-0005).
 *
 * Pure presenter — рендерит layout shell (P-046) + VideoButton (entry P-040)
 * + «Создать договор» button + conditional text-указатель + 5 P-021 states.
 *
 * **Phase 6.5G hotfix changes (Aibek visual verify round 6 fix #3 + #4):**
 *   - Header title «Мои договоры» → **«Договор»** (fix #4 — sweep по всей
 *     функции Договор на мебель).
 *   - **Удалена** кнопка «Открыть договор» (fix #3) — это не action, это
 *     label-указатель. Заменена на conditional `<p>` text:
 *       - `status === 'success'` (есть договоры) → «Выбери договор» подсказка
 *         над списком.
 *       - `status === 'empty'` → «У вас нет сохранённых договоров».
 *       - `status === 'loading' / 'error'` → ничего (state-specific UI рендерит
 *         свой message).
 *   - Props `onOpenClick` + `isEmpty` УДАЛЕНЫ (Container больше не передаёт).
 *   - Anchor `id="contracts-list-region"` сохраняется (cosmetic; не мешает).
 *
 * **Phase 6.5G A8 (preserved):**
 *   - «Создать договор» button → opens `ChoiceSheetCreateMode` через
 *     `onCreateClick` callback (Container mounts sheet + handles navigation).
 *
 * **4 states** (P-021 «idle» сворачивается в «loading»):
 *   - `loading`  — fetching listSavedContracts (skeleton text).
 *   - `success`  — non-empty array, рендерим список карточек.
 *   - `empty`    — успешный fetch + 0 договоров.
 *   - `error`    — fetch threw exception (IDB unavailable etc).
 *
 * Reference:
 *   - ADR-0025 Amendment A8.7 (Option B rationale)
 *   - PRD §10.13.1
 *   - `apps/web/src/features/contract/components/ChoiceSheetCreateMode.tsx`
 */

import { Plus } from 'lucide-react';

import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { VideoButton } from '@/components/VideoButton';

import { ContractCard } from './components/ContractCard';
import { CONTRACT_VIDEO_URL } from './contractVideoUrl';
import type { SavedContract } from './state/savedContractsIdb';

export type ContractListStatus = 'loading' | 'success' | 'empty' | 'error';

export interface ContractListViewProps {
  status: ContractListStatus;
  contracts?: readonly SavedContract[];
  /** Retry handler для error state. */
  onRetry?: () => void;
  /**
   * Phase 6.5G A8 — «Создать договор» button → opens ChoiceSheetCreateMode
   * (Container mounts sheet).
   */
  onCreateClick: () => void;
  /**
   * Phase 7 hotfix round 7 (R7-#2, 2026-05-26) — delete контракта callback,
   * forwarded в ContractCard. Container вызывает `deleteSavedContract(id)` +
   * refetch list.
   */
  onDelete: (id: string) => void;
}

const PRIMARY_CTA_CLASS =
  'w-full min-h-tap rounded-2xl bg-cta text-ink text-base font-semibold flex items-center justify-center gap-2 py-3 ' +
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90';

export function ContractListView({
  status,
  contracts = [],
  onRetry,
  onCreateClick,
  onDelete,
}: ContractListViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Договор" />

      <section
        className="flex-1 overflow-y-auto pb-6 pt-3 flex flex-col gap-4"
        aria-label="Список договоров"
      >
        <div className="mx-3 mt-3">
          <VideoButton videoUrl={CONTRACT_VIDEO_URL} />
        </div>

        {/* Phase 6.5G hotfix fix #3 — единственная primary action «Создать
            договор». «Открыть договор» button удалена; вместо неё conditional
            text-указатель под списком (см. ниже). */}
        <div className="mx-3 flex flex-col gap-2">
          <button
            type="button"
            onClick={onCreateClick}
            aria-label="Создать договор"
            data-testid="contract-list-create-btn"
            className={PRIMARY_CTA_CLASS}
          >
            <Plus size={20} aria-hidden="true" />
            Создать договор
          </button>
        </div>

        {/* Phase 6.5G hotfix fix #3 — text-указатель вместо «Открыть договор»
            button. Conditional на status:
              - success (есть договоры) → «Выбери договор» подсказка над списком
              - empty (пусто) → «У вас нет сохранённых договоров»
              - loading/error → ничего (см. state-specific UI ниже) */}
        {status === 'success' ? (
          <p
            data-testid="contract-list-open-helper"
            className="mx-3 text-sm text-ink/60 text-center m-0"
          >
            Выбери договор
          </p>
        ) : null}
        {status === 'empty' ? (
          <p
            data-testid="contract-list-open-helper"
            className="mx-3 text-sm text-ink/60 text-center m-0"
          >
            У вас нет сохранённых договоров
          </p>
        ) : null}

        <section
          id="contracts-list-region"
          className="mx-3 flex flex-col gap-3"
          data-testid="contract-list-region"
          aria-label="Сохранённые договоры"
        >
          {status === 'loading' ? (
            <p
              role="status"
              data-testid="contract-list-loading"
              className="text-ink/60 text-center py-8"
            >
              Загружаем договоры…
            </p>
          ) : null}

          {status === 'empty' ? (
            <div
              role="status"
              data-testid="contract-list-empty"
              className="bg-card border-[1.5px] border-accent rounded-2xl px-5 py-10 text-center"
            >
              <p className="text-ink text-base m-0">У вас пока нет договоров.</p>
              <p className="text-ink/60 text-sm mt-2 m-0">
                Создайте первый договор или импортируйте из коммерческого предложения.
              </p>
            </div>
          ) : null}

          {status === 'error' ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <p
                role="alert"
                data-testid="contract-list-error"
                className="text-ink text-base text-center m-0"
              >
                Не удалось загрузить договоры. Попробуйте позже.
              </p>
              {onRetry ? (
                <button
                  type="button"
                  onClick={onRetry}
                  aria-label="Повторить загрузку договоров"
                  data-testid="contract-list-retry"
                  className="min-h-tap rounded-xl border-[1.5px] border-accent bg-card text-ink text-base font-medium px-6 py-2 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
                >
                  Повторить
                </button>
              ) : null}
            </div>
          ) : null}

          {status === 'success' ? (
            <ul
              role="list"
              data-testid="contract-list-items"
              className="flex flex-col gap-3 m-0 p-0"
            >
              {contracts.map((contract) => (
                <li key={contract.id} className="list-none">
                  <ContractCard contract={contract} onDelete={onDelete} />
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      </section>

      <BottomBar variant="default" />
    </main>
  );
}
